import { stringify } from 'qs';

import type { ProfileSummaryInfo } from '@zoomit/web-kit';
import { ApiManager } from '@zoomit/web-kit';

import { Article, Profile, UserProfiles } from '../../constants/apis';
import type { GroupingSearchItem } from '../../types/groupings';
import type {
  AuthorSearchItem,
  BookmarksPagination,
  BrowseArticleApiParams,
  BrowseArticlePagination,
} from '../../types/profile';

export const getUserProfileInfo = async (userId: string) => {
  return await ApiManager.get<ProfileSummaryInfo>(`${UserProfiles}/${userId}/popover`);
};

export const getUserBookmarkChips = async (userId: string) => {
  return await ApiManager.get<{ id: string; name: string }[]>(`${Profile}/bookmark-lists`, {
    params: {
      UserId: userId,
    },
  });
};

export const getSelectedChipBookmarks = async ({
  bookmarkChipId,
  pageNumber = 1,
  pageSize = 10,
}: {
  bookmarkChipId?: string;
  pageNumber?: number;
  pageSize?: number;
}) => {
  return await ApiManager.get<BookmarksPagination>(`${Profile}/bookmark-lists/${bookmarkChipId}`, {
    params: {
      id: bookmarkChipId,
      PageNumber: pageNumber,
      PageSize: pageSize,
    },
    paramsSerializer: (params) => {
      return stringify(params);
    },
  });
};

export const browseArticles = async ({
  pageNumber,
  pageSize,
  groupings,
  authors,
}: BrowseArticleApiParams) => {
  return await ApiManager.get<BrowseArticlePagination>(`${Article}/browse`, {
    params: {
      PageNumber: pageNumber,
      PageSize: pageSize,
      Groupings: groupings,
      Authors: authors,
    },
    paramsSerializer: (params) => {
      return stringify(params);
    },
  });
};

export const followArticleCollection = async (collection: GroupingSearchItem, token: string) => {
  return await ApiManager.post(
    `${UserProfiles}/follow-article-collection`,
    { topicId: collection.id },
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const unfollowArticleCollection = async (collection: GroupingSearchItem, token: string) => {
  return await ApiManager.post(
    `${UserProfiles}/unfollow-article-collection`,
    { topicId: collection.id },
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const followCategory = async (categoryId: string, token: string) => {
  return await ApiManager.post(
    `${UserProfiles}/follow-category`,
    { topicId: categoryId },
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const unfollowCategory = async (categoryId: string, token: string) => {
  return await ApiManager.post(
    `${UserProfiles}/unfollow-category`,
    { topicId: categoryId },
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const checkCategoryFollowStatus = async (topicId: string, token: string) => {
  return await ApiManager.get(`${UserProfiles}/following-categories/${topicId}/status`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const followEvent = async (event: GroupingSearchItem, token: string) => {
  return await ApiManager.post(
    `${UserProfiles}/follow-event`,
    { topicId: event.id },
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const unfollowEvent = async (event: GroupingSearchItem, token: string) => {
  return await ApiManager.post(
    `${UserProfiles}/unfollow-event`,
    { topicId: event.id },
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const followAuthor = async (author: AuthorSearchItem, token: string) => {
  return await ApiManager.post(
    `${UserProfiles}/follow-author`,
    { topicId: author.userId },
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const unfollowAuthor = async (author: AuthorSearchItem, token: string) => {
  return await ApiManager.post(
    `${UserProfiles}/unfollow-author`,
    { topicId: author.userId },
    { headers: { Authorization: `Bearer ${token}` } },
  );
};
